/**
 * Phase-3 HQ bay facade and exterior height proxy.
 *
 * This is presentation-only. It owns no Portal/runtime reads and creates no
 * light or skybox: the scene's fixed KEY_SUN and shared HQ skybox remain the
 * single owners of those expensive resources. P7 receives stable materials and
 * a disabled completion-boat pool through the returned handles.
 */
import * as pc from 'playcanvas'
import { registerCollider } from '../colliders'
import { worldRoot } from './worldRoot'
import {
  HQ_CLOUDS,
  HQ_COMPLETION_BOAT_CAP,
  HQ_CURTAIN_MULLIONS,
  HQ_CURTAIN_PANELS,
  HQ_FACADE_BATCH_GROUPS,
  HQ_FACADE_LEVELS,
  HQ_FACADE_MAX_Z,
  HQ_FACADE_MIN_Z,
  HQ_FACADE_MODULE,
  HQ_FACADE_X,
  HQ_SKYLINE,
  HQ_SPANDREL_HEIGHT,
  HQ_STOREY_HEIGHT,
  HQ_VISION_HEAD_OFFSET,
  HQ_WATER_MAX_X,
  HQ_WATER_MIN_X,
  HQ_WATER_Y,
} from './hqFacadeLayout'

export interface HqFacadeHandles {
  /** Stable P7 seam: daemon health recolours this one shared material. */
  healthSpandrelMaterial: pc.StandardMaterial
  /** Stable P7 seam: queue/run activity may tint this bounded window strip. */
  skylineWindowMaterial: pc.StandardMaterial
  /** Disabled roots; P7 leases a slot for each completed-run crossing. */
  completionBoatPool: readonly pc.Entity[]
  /** Single shared bay-glass material; receives the fixed HQ reflection map. */
  glassMaterial: pc.StandardMaterial
  /** Authoritative local-space anchor for the one HQ reflection probe. */
  reflectionProbe: pc.Entity
}

function material(
  diffuse: pc.Color,
  metalness: number,
  gloss: number,
  emissive?: pc.Color,
  emissiveIntensity = 1,
): pc.StandardMaterial {
  const value = new pc.StandardMaterial()
  value.diffuse = diffuse
  value.useMetalness = true
  value.metalness = metalness
  value.gloss = gloss
  if (emissive) {
    value.emissive = emissive
    value.emissiveIntensity = emissiveIntensity
  }
  value.update()
  return value
}

function transparentMaterial(
  diffuse: pc.Color,
  opacity: number,
  cull: number = pc.CULLFACE_NONE,
): pc.StandardMaterial {
  const value = material(diffuse, 0, 0.92)
  value.opacity = opacity
  value.blendType = pc.BLEND_NORMAL
  value.depthWrite = false
  value.cull = cull
  value.update()
  return value
}

function primitive(
  parent: pc.Entity,
  name: string,
  type: 'box' | 'plane',
  position: readonly [number, number, number],
  scale: readonly [number, number, number],
  mat: pc.Material,
  batchGroupId?: number,
): pc.Entity {
  const entity = new pc.Entity(name)
  entity.addComponent('render', { type })
  entity.setLocalPosition(position[0], position[1], position[2])
  entity.setLocalScale(scale[0], scale[1], scale[2])
  if (entity.render) {
    entity.render.castShadows = false
    if (batchGroupId !== undefined) entity.render.batchGroupId = batchGroupId
    for (const instance of entity.render.meshInstances) instance.material = mat
  }
  parent.addChild(entity)
  return entity
}

type BatchGroupIds = ReadonlyMap<string, number>

function createBatchGroups(app: pc.Application): BatchGroupIds {
  const batcher = (app as pc.Application & {
    batcher?: Pick<pc.BatchManager, 'addGroup'>
  }).batcher
  if (!batcher) return new Map()

  const ids = new Map<string, number>()
  for (const spec of HQ_FACADE_BATCH_GROUPS) {
    const group = batcher.addGroup(`hq-facade:${spec.key}`, false, spec.maxAabbSize)
    ids.set(spec.key, group.id)
  }
  return ids
}

function buildCurtainWall(
  root: pc.Entity,
  glass: pc.Material,
  bronze: pc.Material,
  parapet: pc.Material,
  healthSpandrel: pc.Material,
  batchGroups: BatchGroupIds,
): void {
  const thickness = 0.10
  for (const panel of HQ_CURTAIN_PANELS) {
    const z = (panel.minZ + panel.maxZ) / 2
    const width = panel.maxZ - panel.minZ
    const paneHeight = panel.maxY - panel.minY
    const batchGroupId = batchGroups.get(`curtain-l${panel.floor}`)
    const pane = primitive(
      root,
      `hq-facade-glass-l${panel.floor}-m${panel.module}`,
      'plane',
      [HQ_FACADE_X, panel.minY + paneHeight / 2, z],
      [paneHeight, 1, width],
      glass,
      batchGroupId,
    )
    // PlayCanvas planes face local +Y. Rotating +90 around Z makes the pane's
    // front face point into the room (-X), so the curtain is a real
    // single-sided sheet from the occupied side instead of a transparent box
    // with two depth-sorted surfaces on every sightline.
    pane.setLocalEulerAngles(0, 0, 90)
    primitive(
      root,
      `hq-facade-parapet-l${panel.floor}-m${panel.module}`,
      'box',
      [HQ_FACADE_X - 0.07, HQ_FACADE_LEVELS[panel.floor].walkY + panel.parapetHeight / 2, z],
      [0.18, panel.parapetHeight, width],
      parapet,
      batchGroupId,
    )
    primitive(
      root,
      `hq-facade-health-spandrel-l${panel.floor}-m${panel.module}`,
      'box',
      [HQ_FACADE_X - 0.05, HQ_FACADE_LEVELS[panel.floor].walkY + HQ_VISION_HEAD_OFFSET + HQ_SPANDREL_HEIGHT / 2, z],
      [0.16, HQ_SPANDREL_HEIGHT, width],
      healthSpandrel,
      batchGroupId,
    )
    const transomY = panel.minY + paneHeight / 2
    primitive(
      root,
      `hq-facade-transom-l${panel.floor}-m${panel.module}`,
      'box',
      [HQ_FACADE_X - 0.04, transomY, z],
      [0.14, 0.12, width],
      bronze,
      batchGroupId,
    )

    // One collision volume per six-metre module, not per decorative layer.
    registerCollider({
      minX: HQ_FACADE_X - thickness,
      maxX: HQ_FACADE_X + thickness,
      minY: HQ_FACADE_LEVELS[panel.floor].walkY,
      maxY: HQ_FACADE_LEVELS[panel.floor].walkY + HQ_STOREY_HEIGHT,
      minZ: panel.minZ,
      maxZ: panel.maxZ,
      blocksSight: false,
    })
  }

  for (const mullion of HQ_CURTAIN_MULLIONS) {
    primitive(
      root,
      `hq-facade-mullion-l${mullion.floor}-z${mullion.z}`,
      'box',
      [HQ_FACADE_X - 0.045, mullion.minY + (mullion.maxY - mullion.minY) / 2, mullion.z],
      [0.12, mullion.maxY - mullion.minY, 0.25],
      bronze,
      batchGroups.get(`curtain-l${mullion.floor}`),
    )
  }
  // On L3 the curtain itself begins at the 0.60 m parapet top, so it is the
  // required glass guard. Adding another inner pane would double transparency.
}

function buildExteriorProxy(
  root: pc.Entity,
  water: pc.Material,
  sunStreak: pc.Material,
  boardwalk: pc.Material,
  seawall: pc.Material,
  skyline: pc.Material,
  skylineWindows: pc.Material,
  cloud: pc.Material,
  batchGroups: BatchGroupIds,
): void {
  primitive(
    root,
    'hq-bay-water',
    'plane',
    [(HQ_WATER_MIN_X + HQ_WATER_MAX_X) / 2, HQ_WATER_Y, 0],
    [HQ_WATER_MAX_X - HQ_WATER_MIN_X, 1, 170],
    water,
  )
  primitive(root, 'hq-bay-sun-streak', 'plane', [118, HQ_WATER_Y + 0.025, -8], [126, 1, 8], sunStreak)

  // Near-field parallax is split into six-metre modules; unlike the single
  // water horizon, each piece can cull independently when looking away.
  const shoreModules = (HQ_FACADE_MAX_Z - HQ_FACADE_MIN_Z) / HQ_FACADE_MODULE
  for (let module = 0; module < shoreModules; module++) {
    const z = HQ_FACADE_MIN_Z + HQ_FACADE_MODULE * (module + 0.5)
    primitive(root, `hq-boardwalk-${module}`, 'box', [HQ_FACADE_X + 6, -0.25, z], [12, 0.5, 6], boardwalk, batchGroups.get('shore'))
    primitive(root, `hq-seawall-${module}`, 'box', [HQ_FACADE_X + 12.15, -4, z], [0.3, 8, 6], seawall, batchGroups.get('shore'))
  }

  for (const [index, building] of HQ_SKYLINE.entries()) {
    const baseY = HQ_WATER_Y
    primitive(
      root,
      `hq-skyline-${building.row}-${index}`,
      'box',
      [building.x, baseY + building.height / 2, building.z],
      [building.depth, building.height, building.width],
      skyline,
      batchGroups.get(`skyline-${building.row}`),
    )
    // A stepped crown breaks the proxy's ruler-line silhouette without adding
    // a material or draw after batching. Twelve bounded caps also keep the
    // total facade allocation at its audited 180-render ceiling.
    const crownHeight = 1.2 + (index % 3) * 0.8
    primitive(
      root,
      `hq-skyline-crown-${building.row}-${index}`,
      'box',
      [building.x, baseY + building.height + crownHeight / 2, building.z],
      [building.depth * (0.45 + (index % 2) * 0.15), crownHeight, building.width * 0.58],
      skyline,
      batchGroups.get(`skyline-${building.row}`),
    )
    // A single strip per silhouette keeps the distant light field bounded.
    primitive(
      root,
      `hq-skyline-lights-${building.row}-${index}`,
      'box',
      [building.x - building.depth / 2 - 0.03, baseY + building.height * 0.58, building.z],
      [0.05, Math.min(1.2, building.height * 0.06), building.width * 0.72],
      skylineWindows,
      batchGroups.get(`skyline-${building.row}`),
    )
  }

  for (const [index, item] of HQ_CLOUDS.entries()) {
    primitive(
      root,
      `hq-cloud-${index}`,
      'box',
      [item.x, item.y, item.z],
      [0.08, item.height, item.width],
      cloud,
      batchGroups.get('cloud-deck'),
    )
  }
}

function buildCompletionBoatPool(
  root: pc.Entity,
  hull: pc.Material,
  sail: pc.Material,
): readonly pc.Entity[] {
  const pool: pc.Entity[] = []
  for (let index = 0; index < HQ_COMPLETION_BOAT_CAP; index++) {
    const boat = new pc.Entity(`hq-completion-boat-${index}`)
    boat.setLocalPosition(38 + index * 6, HQ_WATER_Y + 0.25, -50 + index * 13)
    primitive(boat, `hq-completion-boat-hull-${index}`, 'box', [0, 0, 0], [2.8, 0.35, 0.8], hull)
    const sailEntity = primitive(boat, `hq-completion-boat-sail-${index}`, 'plane', [0, 1.15, 0], [1.6, 1, 1.8], sail)
    sailEntity.setLocalEulerAngles(0, 0, 90)
    boat.enabled = false
    root.addChild(boat)
    pool.push(boat)
  }
  return pool
}

export function buildHqFacade(app: pc.Application): HqFacadeHandles {
  const root = worldRoot(app)
  const batchGroups = createBatchGroups(app)
  const glass = transparentMaterial(
    new pc.Color(0.68, 0.77, 0.80),
    0.28,
    pc.CULLFACE_BACK,
  )
  // The fixed reflection should catch at grazing angles without turning the
  // view into an opaque HDR photograph. Keeping it below the glass opacity
  // preserves the authored exterior as the hero.
  glass.reflectivity = 0.16
  const bronze = material(new pc.Color(0.38, 0.23, 0.10), 0.72, 0.58)
  const parapet = material(new pc.Color(0.23, 0.24, 0.26), 0.1, 0.2)
  const healthSpandrelMaterial = material(
    new pc.Color(0.08, 0.12, 0.12),
    0.35,
    0.35,
    new pc.Color(0.21, 0.75, 0.48),
    1.4,
  )
  const water = material(new pc.Color(0.06, 0.23, 0.34), 0.18, 0.82, new pc.Color(0.02, 0.16, 0.24), 0.55)
  const sunStreak = material(new pc.Color(0.85, 0.58, 0.30), 0, 0.8, new pc.Color(1.0, 0.52, 0.18), 1.4)
  const boardwalk = material(new pc.Color(0.42, 0.25, 0.11), 0, 0.32, new pc.Color(0.14, 0.06, 0.02), 0.35)
  const seawall = material(new pc.Color(0.29, 0.31, 0.32), 0.1, 0.18)
  const skyline = material(new pc.Color(0.07, 0.10, 0.16), 0.15, 0.28, new pc.Color(0.015, 0.025, 0.05), 0.25)
  const skylineWindowMaterial = material(
    new pc.Color(0.36, 0.24, 0.10),
    0,
    0.2,
    new pc.Color(1.0, 0.48, 0.12),
    2.1,
  )
  const clouds = transparentMaterial(new pc.Color(0.84, 0.82, 0.78), 0.18)
  const boatHull = material(new pc.Color(0.16, 0.18, 0.20), 0.15, 0.35)
  const boatSail = material(new pc.Color(0.92, 0.85, 0.72), 0, 0.2)

  // These objects *are* the HQ horizon. District fog reaches full density at
  // 150 m, which otherwise erases the far row at X=200 and turns every floor's
  // bay view back into an infinite sky. Their authored two-depth value and
  // translucency already provide the bounded aerial perspective.
  for (const exteriorMaterial of [
    water,
    sunStreak,
    skyline,
    skylineWindowMaterial,
    clouds,
  ]) {
    exteriorMaterial.useFog = false
    exteriorMaterial.update()
  }

  buildCurtainWall(root, glass, bronze, parapet, healthSpandrelMaterial, batchGroups)
  buildExteriorProxy(
    root,
    water,
    sunStreak,
    boardwalk,
    seawall,
    skyline,
    skylineWindowMaterial,
    clouds,
    batchGroups,
  )
  const completionBoatPool = buildCompletionBoatPool(root, boatHull, boatSail)

  // The fixed golden-hour cubemap is attached asynchronously once the shared
  // environment loader has decoded it. Keeping the probe as an explicit,
  // non-rendering scene anchor makes ownership and box-projection auditable
  // without adding a second camera or a per-frame reflection pass.
  const reflectionProbe = new pc.Entity('hq-facade-reflection-probe')
  reflectionProbe.setLocalPosition(4, 9, 0)
  root.addChild(reflectionProbe)

  return {
    healthSpandrelMaterial,
    skylineWindowMaterial,
    completionBoatPool,
    glassMaterial: glass,
    reflectionProbe,
  }
}

/**
 * Apply the one fixed HQ reflection cubemap to the curtain wall.
 *
 * The cubemap is generated once from the fixed-time-of-day HDR and projected
 * from the atrium anchor. This is deliberately not a live cube camera: the
 * facade contract calls for a baked probe and the runtime draw budget must not
 * pay six extra scene renders.
 */
export function applyHqFacadeReflectionProbe(
  handles: HqFacadeHandles,
  cubemap: pc.Texture,
): void {
  const center = handles.reflectionProbe.getPosition()
  handles.glassMaterial.cubeMap = cubemap
  handles.glassMaterial.cubeMapProjection = pc.CUBEPROJ_BOX
  handles.glassMaterial.cubeMapProjectionBox = new pc.BoundingBox(
    new pc.Vec3(center.x, center.y, center.z),
    new pc.Vec3(HQ_FACADE_X, 9, HQ_FACADE_MAX_Z),
  )
  handles.glassMaterial.update()
}
